import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { VISIBLE_FISHES, TOTAL_COLLECTION, type FishDef } from '@/data/fish'

interface FishDrawerProps {
  isOpen: boolean
  selectedId: string
  onSelect: (fish: FishDef) => void
  onClose: () => void
  unlockedFish: Set<string>
}

export function FishDrawer({ isOpen, selectedId, onSelect, onClose, unlockedFish }: FishDrawerProps) {
  const handleSelect = (fish: FishDef) => {
    onSelect(fish)
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/40 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl rounded-t-[28px] max-h-[70vh] flex flex-col"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h2 className="text-lg font-black tracking-[0.1em] text-[#2f5d8a]">选择鱼儿</h2>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4">
              <div className="grid grid-cols-4 gap-3">
                {VISIBLE_FISHES.map((f) => {
                  const active = f.id === selectedId
                  return (
                    <button
                      key={f.id}
                      onClick={() => handleSelect(f)}
                      className={`flex flex-col items-center gap-2 p-3 rounded-2xl border transition-all duration-300 ${
                        active
                          ? 'bg-sky-50 border-sky-300 shadow-md'
                          : 'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className={`w-16 h-16 rounded-xl flex items-center justify-center overflow-hidden ${active ? 'bg-sky-100' : 'bg-slate-100'}`}>
                        <img src={f.img} alt={f.name} className="w-14 h-14 object-contain drop-shadow" draggable={false} />
                      </div>
                      <span className={`text-xs font-bold tracking-wider text-center ${active ? 'text-[#2f5d8a]' : 'text-slate-600'}`}>
                        {f.name}
                      </span>
                      {active && (
                        <span className="text-[10px] font-bold text-white bg-gradient-to-r from-sky-400 to-blue-500 rounded-full px-2 py-0.5">
                          选中
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="px-4 pb-6 pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-2">
                <span className="tracking-widest">图鉴收集 COLLECTION</span>
                <span className="text-[#2f5d8a] font-black">{unlockedFish.size}/{TOTAL_COLLECTION}</span>
              </div>
              <div className="flex gap-1">
                {Array.from({ length: TOTAL_COLLECTION }, (_, i) => (
                  <span
                    key={i}
                    className={`h-1.5 flex-1 rounded-full ${i < unlockedFish.size ? 'bg-gradient-to-r from-sky-400 to-blue-500' : 'bg-slate-300/50'}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
